import { useMemo } from "react";
import _ from "lodash";
import { resolveFlexHeaders } from "TranscriptsV3/modules/utils";
import {
  CELL_TYPES,
  TRANSCRIPT_DATA_SOURCE,
} from "../../../../../module/constants";

export const CUSTOM_SUFFIX = t => [
  {
    type: "tag",
    color: "yellow",
    value: t("common:custom"),
    height: 20,
    width: 52,
  },
];

export const useGeneralGradingPeriodsSectionData = ({
  academicRecordsData,
  transcriptGradingPeriods,
  getCurrentPeriods,
  containerWidth,
  onHeaderIconClick,
  errorGradeMapIds,
  t,
}) => {
  const headers = useMemo(() => {
    const rawHeaders = [
      {
        id: "gradeLevel",
        title: t("common:grade_level"),
        flex: 1,
      },
      {
        id: "gradingPeriods",
        title: t("transcript:grading_periods_to_display"),
        flex: 1,
        suffixIcon: {
          iconName: "SettingsOutlined",
          isAlignedToRightEnd: true,
          tooltip: t("transcript:configure_custom_grading_periods"),
          onIconClick: onHeaderIconClick,
          testId:
            "transcriptsV3-academicRecords-generalGradingPeriodsSection-settings",
        },
      },
    ];

    return resolveFlexHeaders({
      headers: rawHeaders,
      containerWidth,
    });
  }, [containerWidth, onHeaderIconClick, t]);

  const gridRows = useMemo(() => {
    const grades = _.get(
      academicRecordsData,
      "node.transcriptSummary.grades",
      []
    );

    const customSuffix = CUSTOM_SUFFIX(t);

    const transcriptGradingPeriodsById = _.reduce(
      transcriptGradingPeriods,
      (acc, gp) => {
        const id = String(_.get(gp, "id"));
        if (id) {
          acc[id] = gp;
        }
        return acc;
      },
      {}
    );

    return _.map(grades, gradeMap => {
      const gradeMapId = _.get(gradeMap, "id");
      const gradeName = _.get(gradeMap, "grade.name");
      const dataSource = _.get(gradeMap, "dataSource");

      const schoolSourceId = _.get(gradeMap, "school.sourceId");
      const academicYearSourceId = _.get(gradeMap, "academicYear.sourceId");

      const isCustomGrade = dataSource === TRANSCRIPT_DATA_SOURCE.CUSTOM;

      // Filter GPs by grade type — same pattern as GradeLevels DataGrid
      // Custom grades see only custom GPs; toddle-defined grades see only
      // toddle-defined GPs that match the grade's school + academic year
      let filteredOptions = [];
      let groupLabel = "";

      if (isCustomGrade) {
        filteredOptions = _.map(
          _.filter(transcriptGradingPeriods, gp => _.get(gp, "isCustom")),
          p => ({ id: String(p.id), label: p.name })
        );
        groupLabel = t("common:custom_label", {
          label: t("common:label_lowercase", {
            label: t("common:grading_period_plural"),
          }),
        });
      } else {
        filteredOptions = _.chain(transcriptGradingPeriods)
          .filter(gp => {
            if (_.get(gp, "isCustom")) return false;
            const curriculumProgramId = _.get(
              gp,
              "source.curriculumProgram.id"
            );
            const yearId = _.get(gp, "source.academicYear.id");
            return (
              String(curriculumProgramId) === String(schoolSourceId) &&
              String(yearId) === String(academicYearSourceId)
            );
          })
          .map(p => ({ id: String(p.id), label: p.name }))
          .value();
        groupLabel = t("transcript:toddle_defined_label", {
          label: t("common:label_lowercase", {
            label: t("common:grading_period_plural"),
          }),
        });
      }

      const dropdownOptions = _.isEmpty(filteredOptions)
        ? []
        : [
            {
              id: "group",
              label: groupLabel,
              isDisabled: true,
              options: filteredOptions,
            },
          ];

      // Show ALL periods in the mapping as selected in the dropdown,
      // regardless of isEnabled. The dropdown controls add/remove only;
      // isEnabled is toggled separately in the per-grade tab.
      const draftOrBackendPeriods = getCurrentPeriods(gradeMapId) || [];
      const currentPeriods = _.map(draftOrBackendPeriods, p => {
        const periodData = _.get(
          transcriptGradingPeriodsById,
          String(p.gradingPeriodId)
        );
        return {
          id: String(p.gradingPeriodId),
          label: periodData ? periodData.name : p.gradingPeriodId,
        };
      });

      const columns = [
        {
          colId: "gradeLevel",
          cellType: CELL_TYPES.LABEL,
          value: gradeName,
          suffix: {
            isAlignedToRightEnd: false,
            items: isCustomGrade ? customSuffix : [],
          },
        },
        {
          colId: "gradingPeriods",
          cellType: CELL_TYPES.TAG,
          value: currentPeriods,
          isEditable: true,
          isMulti: true,
          selectAllEnabled: false,
          isSelectAllEnabled: false,
          maxMenuHeight: 200,
          validInputs: { options: dropdownOptions },
          checkboxAlignment: "right",
          addNewOptionEnabled: isCustomGrade,
          placeholder: t("common:select_with_label", {
            label: t("common:grading_period_plural"),
          }),
          hasError: _.includes(errorGradeMapIds, gradeMapId),
        },
      ];

      return {
        rowId: gradeMapId,
        columns,
      };
    });
  }, [
    academicRecordsData,
    transcriptGradingPeriods,
    getCurrentPeriods,
    errorGradeMapIds,
    t,
  ]);

  return { headers, gridRows };
};
